/**
 * Папка, которую забирает облако.
 *
 * Прямая выгрузка в Google Диск или Яндекс Диск требует регистрации
 * приложения, экрана согласия и токенов, которые всё равно живут час, — то
 * есть «копия, пока человек спит» так не получается. Зато у обоих облаков есть
 * папка на диске, и её содержимое уезжает наверх само.
 *
 * Поэтому человек один раз указывает папку — свою, любую, хоть на флешке, — а
 * приложение молча кладёт туда копию при первом за сутки открытии. Ни токенов,
 * ни доступа к чужому аккаунту, ни нашего сервера: дальше работает клиент
 * облака, и данные не покидают устройство иначе как в собственное хранилище
 * человека.
 *
 * ## Что кладётся в папку
 *
 * Два файла, и оба — обычный JSON:
 *
 * - `kairo-backup.json` — последнее состояние, перезаписывается каждый день;
 * - `kairo-backup-2026-08.json` — первая копия месяца, больше не трогается.
 *
 * Месячные нужны затем же, зачем и внутри приложения: беду замечают не сразу, и
 * ежедневно перезаписываемый файл к тому времени уже испорчен. Чужие файлы при
 * этом не удаляются никогда — папка принадлежит человеку, а не нам.
 *
 * ## Про разрешение
 *
 * Браузер помнит выбранную папку, но доступ к ней может потребовать
 * подтверждения после перезапуска. Молча спросить нельзя — диалог открывается
 * только по нажатию, — поэтому приложение не пытается: пока доступа нет, копия
 * ложится внутрь приложения, а карточка предлагает подтвердить.
 */

import { db } from '@/shared/db/database';

import type { Backup } from './export-data';
import { INTEGRITY_MESSAGE } from './integrity';

/**
 * Объявления, которых нет в типах браузера.
 *
 * `FileSystemDirectoryHandle` описан, а выбор папки и проверка разрешения —
 * нет: они пока за пределами стандарта, который знает TypeScript.
 */
interface PermissionOptions {
  mode?: 'read' | 'readwrite';
}

interface DirectoryHandle extends FileSystemDirectoryHandle {
  queryPermission?: (options?: PermissionOptions) => Promise<PermissionState>;
  requestPermission?: (options?: PermissionOptions) => Promise<PermissionState>;
}

declare global {
  interface Window {
    showDirectoryPicker?: (options?: {
      mode?: 'read' | 'readwrite';
      id?: string;
      startIn?: string;
    }) => Promise<FileSystemDirectoryHandle>;
  }
}

const FOLDER_KEY = 'backup.folder';

/** Имя файла с последним состоянием — то, что уезжает в облако каждый день. */
export const FOLDER_LATEST_FILE = 'kairo-backup.json';

/** Имя месячной копии: `kairo-backup-2026-08.json`. */
export function folderMonthlyFile(exportedAt: string): string {
  return `kairo-backup-${exportedAt.slice(0, 7)}.json`;
}

export interface BackupFolder {
  handle: DirectoryHandle;
  /** Имя папки — единственное, что о ней можно показать человеку. */
  name: string;
}

/** Умеет ли браузер отдавать папку приложению. */
export function isFolderSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/**
 * Спрашивает папку у человека и запоминает её.
 *
 * `null` — человек закрыл диалог: это обычный отказ, а не сбой.
 */
export async function chooseBackupFolder(): Promise<BackupFolder | null> {
  if (!isFolderSupported()) return null;

  let handle: FileSystemDirectoryHandle;
  try {
    handle = await window.showDirectoryPicker!({ mode: 'readwrite', id: 'kairo-backups' });
  } catch {
    return null;
  }

  const folder: BackupFolder = { handle, name: handle.name };
  await db.settings.put({ key: FOLDER_KEY, value: folder, updatedAt: Date.now() });

  return folder;
}

export async function readBackupFolder(): Promise<BackupFolder | null> {
  const row = await db.settings.get(FOLDER_KEY);
  const value = row?.value as BackupFolder | undefined;

  return value?.handle ? value : null;
}

export async function forgetBackupFolder(): Promise<void> {
  await db.settings.delete(FOLDER_KEY);
}

/**
 * Состояние доступа к папке.
 *
 * Отсутствие метода — не беда: так ведут себя хранилища, которым разрешение не
 * нужно вовсе, и отказывать им незачем.
 */
export async function folderPermission(folder: BackupFolder): Promise<PermissionState> {
  if (!folder.handle.queryPermission) return 'granted';
  return folder.handle.queryPermission({ mode: 'readwrite' });
}

/** Спрашивает доступ заново. Вызывается только из обработчика нажатия. */
export async function requestFolderPermission(folder: BackupFolder): Promise<PermissionState> {
  if (!folder.handle.requestPermission) return 'granted';
  return folder.handle.requestPermission({ mode: 'readwrite' });
}

/**
 * Кладёт копию в папку.
 *
 * Месячный файл пишется только раз: если он уже есть, его не трогают — в нём
 * состояние на начало месяца, и перезаписать его сегодняшним значило бы
 * потерять единственную точку возврата в глубину.
 */
export async function writeBackupToFolder(
  folder: BackupFolder,
  backup: Backup,
  content: string,
): Promise<void> {
  await writeFile(folder.handle, FOLDER_LATEST_FILE, content);

  const monthly = folderMonthlyFile(backup.exportedAt);
  if (!(await fileExists(folder.handle, monthly))) {
    await writeFile(folder.handle, monthly, content);
  }
}

/**
 * Пишет файл и тут же перечитывает написанное.
 *
 * Половина беды резервных копий — в том, что запись не жалуется. Место на диске
 * кончилось, флешку выдернули, клиент облака перехватил файл в момент записи —
 * а в папке остаётся правдоподобный огрызок с приличным именем. Перечитать
 * стоит доли секунды и снимает вопрос: копия либо есть, либо о ней сказано.
 */
async function writeFile(handle: DirectoryHandle, name: string, data: string): Promise<void> {
  const file = await handle.getFileHandle(name, { create: true });
  const writable = await file.createWritable();

  try {
    await writable.write(data);
  } finally {
    await writable.close();
  }

  // Сравнение с исходной строкой, а не разбор написанного: так ловится и
  // обрыв, и лишний хвост от прежнего, более длинного файла.
  const written = await (await handle.getFileHandle(name)).getFile();
  if ((await written.text()) !== data) throw new Error(INTEGRITY_MESSAGE.checksum);
}

async function fileExists(handle: DirectoryHandle, name: string): Promise<boolean> {
  try {
    await handle.getFileHandle(name);
    return true;
  } catch {
    return false;
  }
}
